// World ↔ screen coordinate transform.
//
// World convention: y-up, units = "cells" (1 cell = 1 world unit, the
// same unit the server uses for player positions). The world origin is
// at world coords (0, 0); the world is bounded by `[-500, +500]` per the
// constants in `shared::protocol` (which the wasm bundle exposes via
// `world_min_x()` etc.).
//
// Screen convention: y-down (HTML5 Canvas standard), units = CSS pixels.
// We flip y in `worldToScreen` so positive world-y renders upward, like
// every game player on earth expects.
//
// The camera state is just `(center, pixelsPerWorldUnit)`. No rotation
// in Phase 1 — it adds matrix math without buying anything for an .io.

export interface Camera {
  /** World-space coordinate at the visual center of the canvas. */
  centerX: number;
  centerY: number;
  /** Zoom factor. Larger = more pixels per cell = more zoomed in. At
   *  32 px/cell, a 1080p canvas shows ~60 cells horizontally and ~34
   *  vertically — matches the server's 30-cell AOI radius (60-cell
   *  diameter) almost exactly, so the player sees their full AOI on
   *  screen and there's no fog-of-war pop-in at the edges. The single
   *  knob most likely to be tweaked; bump higher for tighter combat
   *  view, lower for more map awareness. */
  pixelsPerWorldUnit: number;
}

export interface ViewportSize {
  /** CSS pixels. */
  width: number;
  height: number;
  /** devicePixelRatio at draw time. We bake the multiply into the
   *  canvas backing-store but keep the math in CSS px. */
  dpr: number;
}

export function makeCamera(): Camera {
  return { centerX: 0, centerY: 0, pixelsPerWorldUnit: 32 };
}

/** Transform a world point into CSS-pixel coordinates relative to the
 *  canvas top-left. `out` is filled in place and returned to avoid
 *  per-call object allocation in the render hot path. */
export function worldToScreen(
  cam: Camera,
  size: ViewportSize,
  worldX: number,
  worldY: number,
  out: { x: number; y: number },
): { x: number; y: number } {
  const dx = worldX - cam.centerX;
  const dy = worldY - cam.centerY;
  out.x = size.width * 0.5 + dx * cam.pixelsPerWorldUnit;
  out.y = size.height * 0.5 - dy * cam.pixelsPerWorldUnit; // y-up → y-down
  return out;
}

/** Inverse of `worldToScreen`. Useful for mouse → aim-vector math in
 *  step 14, but lives here so the transform stays in one place. */
export function screenToWorld(
  cam: Camera,
  size: ViewportSize,
  screenX: number,
  screenY: number,
  out: { x: number; y: number },
): { x: number; y: number } {
  out.x = cam.centerX + (screenX - size.width * 0.5) / cam.pixelsPerWorldUnit;
  out.y = cam.centerY - (screenY - size.height * 0.5) / cam.pixelsPerWorldUnit;
  return out;
}

/** Visible world rectangle. Useful for culling and grid drawing — we
 *  only stroke grid lines that fall inside this window. */
export interface WorldRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function visibleWorldRect(
  cam: Camera,
  size: ViewportSize,
  out: WorldRect,
): WorldRect {
  const halfW = size.width * 0.5 / cam.pixelsPerWorldUnit;
  const halfH = size.height * 0.5 / cam.pixelsPerWorldUnit;
  out.minX = cam.centerX - halfW;
  out.maxX = cam.centerX + halfW;
  out.minY = cam.centerY - halfH;
  out.maxY = cam.centerY + halfH;
  return out;
}
